"use client";

// Tiny client-side helper for rendering an i18n key inside an otherwise
// server-rendered tree. The substrate's page-factory builds JSX in a server
// component (so it can read the entity registry + DB) and emits raw i18n
// keys for titles / labels — those keys can't be resolved on the server
// because our I18nProvider is the `NextIntlClientProvider`, which is
// client-only. Wrapping the key in `<TranslatedText id="..." />` defers
// resolution to render time.
//
// Resolution policy:
//   1. Try the flat-key bundle (`useFlatT()`) — module bundles ship there.
//   2. Fall through to `useTranslations()` — platform bundle (nested) lives
//      there.
//   3. Render the optional `fallback`, or the raw key when no fallback set.

import * as React from "react";
import { useT } from "./flat-keys-provider";

export interface TranslatedTextProps {
  /** Dotted i18n key (e.g. `"fleet.machine"`). */
  id: string;
  /** Optional fallback rendered when the key returns the raw id. */
  fallback?: React.ReactNode;
}

export function TranslatedText({ id, fallback }: TranslatedTextProps): React.ReactNode {
  const t = useT();
  const value = t(id);
  if (value === id && fallback !== undefined) return fallback;
  return value;
}
